/**
 * Keyboard hints (Settings > Keyboard hints): which key comes next, and which
 * finger types it, by the standard touch-typing finger map.
 *
 * Pure: no Phaser. The on-screen keyboard (scenes/game/KeyboardHint.ts) draws
 * what this returns.
 */
import { charMatches, requiredPart } from './TypingInput';
import type { Match } from './TypingInput';

export type Finger = 'pinky' | 'ring' | 'middle' | 'index' | 'thumb';
export type Hand = 'left' | 'right';

export interface FingerInfo {
  readonly hand: Hand;
  readonly finger: Finger;
}

/** The three letter rows of a US QWERTY keyboard, as drawn. */
export const KEY_ROWS: readonly (readonly string[])[] = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

/** Standard home-row touch typing: each column belongs to one finger. */
const COLUMNS: Readonly<Record<string, FingerInfo>> = (() => {
  const map: Record<string, FingerInfo> = {};
  const assign = (keys: string, hand: Hand, finger: Finger): void => {
    for (const key of keys) map[key] = { hand, finger };
  };
  assign('qaz', 'left', 'pinky');
  assign('wsx', 'left', 'ring');
  assign('edc', 'left', 'middle');
  assign('rfvtgb', 'left', 'index');
  assign('yhnujm', 'right', 'index');
  assign('ik', 'right', 'middle');
  assign('ol', 'right', 'ring');
  assign('p', 'right', 'pinky');
  return map;
})();

/** Which finger types a letter, or null for anything that isn't a letter key. */
export function fingerFor(key: string): FingerInfo | null {
  if (key === 'Space' || key === 'Enter') {
    return key === 'Space'
      ? { hand: 'right', finger: 'thumb' }
      : { hand: 'right', finger: 'pinky' };
  }
  if (key === 'Backspace') return { hand: 'right', finger: 'pinky' };
  return COLUMNS[key.toLowerCase()] ?? null;
}

export interface KeyHint {
  /** A lowercase letter, or 'Backspace' / 'Enter'. */
  readonly key: string;
  /** Shift on the other hand from the letter, for a capital. */
  readonly shift: Hand | null;
  readonly finger: FingerInfo;
}

/**
 * The key to press next to make progress on `target`.
 *
 * A wrong letter anywhere in what has been typed means Backspace first, so a
 * child learns to fix a slip rather than type on past it. A finished word
 * needs Enter only when auto enter is off. Returns null when there is nothing
 * sensible to suggest.
 */
export function nextKeyHint(
  typed: string,
  target: string,
  autoEnter: boolean,
  match: Match = 'strict',
): KeyHint | null {
  if (target === '') return null;
  const goodPrefix = [...typed].every((char, i) => charMatches(char, target[i], match));
  // Lenient: a final full stop is optional, so the word is done without it.
  const required = requiredPart(target, match);
  if (goodPrefix && typed.length >= required.length && typed.length < target.length) {
    return autoEnter
      ? null
      : { key: 'Enter', shift: null, finger: { hand: 'right', finger: 'pinky' } };
  }
  if (!goodPrefix) {
    return { key: 'Backspace', shift: null, finger: { hand: 'right', finger: 'pinky' } };
  }
  if (typed.length >= target.length) {
    if (autoEnter) return null;
    return { key: 'Enter', shift: null, finger: { hand: 'right', finger: 'pinky' } };
  }
  const char = target[typed.length] ?? '';
  const finger = fingerFor(char);
  if (finger === null) return null;
  const capital = char !== char.toLowerCase();
  // Touch typing holds Shift with the hand that is not typing the letter.
  const shift: Hand | null = capital ? (finger.hand === 'left' ? 'right' : 'left') : null;
  return { key: char.toLowerCase(), shift, finger };
}

/** "Left index", "Right pinky", "Thumb" - the words shown under the keyboard. */
export function fingerLabel(info: FingerInfo): string {
  if (info.finger === 'thumb') return 'Thumb';
  const hand = info.hand === 'left' ? 'Left' : 'Right';
  return `${hand} ${info.finger}`;
}
